import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { User as UserIcon, School as SchoolIcon, Phone, Save, LogOut, ShieldCheck, KeyRound, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import PersonalizePicker from "../components/PersonalizePicker";
import { findAvatar } from "../lib/personalize";

export default function Profile() {
  const nav = useNavigate();
  const { user, loading, updateMe, personalize, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: "", school_name: "", udise: "", email: "" });
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [persBusy, setPersBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (user) setForm({
      full_name: user.full_name || "",
      school_name: user.school_name || "",
      udise: user.udise || "",
      email: user.email || "",
    });
  }, [user]);

  if (loading || !user) {
    return <div className="p-10 text-center text-slate-500" data-testid="profile-loading">Loading your profile…</div>;
  }

  const saveProfile = async () => {
    if (form.udise && !/^\d{11}$/.test(form.udise)) return toast.error("UDISE must be exactly 11 digits");
    setSaving(true);
    try {
      await updateMe({
        full_name: form.full_name.trim(),
        school_name: form.school_name.trim(),
        udise: form.udise.trim(),
        email: form.email.trim(),
      });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw.current) return toast.error("Enter current password");
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match");
    setPwBusy(true);
    try {
      await api.changePassword(pw.current, pw.next);
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
      setPwOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed");
    } finally { setPwBusy(false); }
  };

  const savePersonalize = async ({ lucky_number, avatar_id }) => {
    setPersBusy(true);
    try {
      await personalize(lucky_number, avatar_id);
      toast.success("Personalization updated");
      setPersonalizeOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setPersBusy(false); }
  };

  const doLogout = () => { logout(); toast.info("Signed out"); nav("/"); };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="chip chip-green"><UserIcon size={14}/> Profile</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-slate-900">{user.full_name || "My account"}</h1>
          <p className="mt-1 text-slate-600 flex items-center gap-1"><Phone size={12}/> {user.mobile}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="chip chip-slate" data-testid="profile-plan">Free plan · Premium ₹299/year (Launch Offer)</span>
          <Button variant="outline" onClick={doLogout} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="profile-logout">
            <LogOut size={14}/> Sign out
          </Button>
        </div>
      </div>

      {/* School details */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="profile-details">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-emerald-700 text-white grid place-items-center"><SchoolIcon size={14}/></span>
          <h2 className="font-display font-bold text-lg text-slate-900">School details</h2>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="p-name" className="text-sm font-semibold">Full name</Label>
            <Input id="p-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                   className="h-11 mt-1.5" data-testid="profile-name"/>
          </div>
          <div>
            <Label htmlFor="p-mobile" className="text-sm font-semibold">Mobile number</Label>
            <Input id="p-mobile" value={user.mobile} disabled className="h-11 mt-1.5 bg-slate-50" data-testid="profile-mobile"/>
          </div>
          <div>
            <Label htmlFor="p-school" className="text-sm font-semibold">School name</Label>
            <Input id="p-school" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                   className="h-11 mt-1.5" placeholder="e.g. Govt. UP School, Bhubaneswar" data-testid="profile-school"/>
          </div>
          <div>
            <Label htmlFor="p-udise" className="text-sm font-semibold">UDISE code (11 digits)</Label>
            <Input id="p-udise" value={form.udise} maxLength={11} inputMode="numeric"
                   onChange={(e) => setForm({ ...form, udise: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                   className="h-11 mt-1.5" placeholder="21010100999" data-testid="profile-udise"/>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="p-email" className="text-sm font-semibold">Email (optional)</Label>
            <Input id="p-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="h-11 mt-1.5" placeholder="for receipts and support" data-testid="profile-email"/>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={saveProfile} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="profile-save">
            <Save size={14}/> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Security */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="profile-security">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-emerald-700 text-white grid place-items-center"><ShieldCheck size={14}/></span>
          <h2 className="font-display font-bold text-lg text-slate-900">Security</h2>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-1"><KeyRound size={14}/> Password</div>
              <div className="text-xs text-slate-500">Change your account password.</div>
            </div>
            <Button variant="outline" onClick={() => setPwOpen((v) => !v)} data-testid="profile-toggle-pw">{pwOpen ? "Cancel" : "Change password"}</Button>
          </div>
          {pwOpen && (
            <div className="grid sm:grid-cols-3 gap-3">
              <Input type="password" placeholder="Current" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="h-11" data-testid="profile-pw-current"/>
              <Input type="password" placeholder="New (min 6)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="h-11" data-testid="profile-pw-new"/>
              <Input type="password" placeholder="Confirm new" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="h-11" data-testid="profile-pw-confirm"/>
              <div className="sm:col-span-3 flex justify-end">
                <Button onClick={changePw} disabled={pwBusy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="profile-pw-save">
                  {pwBusy ? "Saving…" : "Update password"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-1"><Sparkles size={14}/> Recovery setup</div>
              <div className="text-xs text-slate-500">
                Your recovery number + image, used to verify account ownership.{" "}
                {user.personalized ? (
                  <span className="text-emerald-700">
                    ✅ Completed
                    {user.avatar_id ? <> · <span aria-hidden>{findAvatar(user.avatar_id)?.emoji}</span> {findAvatar(user.avatar_id)?.label || user.avatar_id}</> : null}
                  </span>
                ) : (
                  <span className="text-amber-700">⚠️ Not chosen yet.</span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => setPersonalizeOpen((v) => !v)} data-testid="profile-toggle-personalize">
              {personalizeOpen ? "Cancel" : user.personalized ? "Change" : "Choose now"}
            </Button>
          </div>
          {personalizeOpen && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2 sm:p-4">
              <PersonalizePicker
                title="Update your recovery setup"
                subtitle="Pick a new number and image"
                helper="One number and one image — used to verify you if you ever need help with your account."
                submitLabel="Save changes"
                busy={persBusy}
                initial={{ lucky_number: null, avatar_id: user.avatar_id }}
                onSubmit={savePersonalize}
              />
            </div>
          )}

          <div className="pt-2 border-t border-slate-100 mt-2 text-xs text-slate-500">
            <b>Forgot password?</b> Reach the PM Poshan admin on WhatsApp or call — self-serve recovery has been retired for extra safety.
          </div>
        </div>
      </div>

      {/* Premium teaser */}
      <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-6" data-testid="profile-premium-teaser">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-lg bg-orange-500 text-white grid place-items-center shrink-0"><Sparkles size={18}/></span>
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg text-orange-900">Unlock Premium — Launch Offer ₹299 / year</h2>
            <p className="text-sm text-orange-900/80">Save daily entries, view history, run Weekly / Monthly / Annual reports and Bulk PDFs. Coming in Phase 3 via Razorpay.</p>
          </div>
          <Button disabled className="bg-orange-500 hover:bg-orange-600 text-white opacity-70" data-testid="profile-upgrade">
            Coming soon
          </Button>
        </div>
      </div>
    </div>
  );
}
